import { createApi, fetchBaseQuery, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { generatePath } from "react-router-dom";
import { getAccessToken } from "src/state/modules/auth-token";
import * as Eden from "src/eden/content";
import * as Team from "src/state/modules/team";
import * as Event from "src/state/modules/event";
import { mapEdenTeamToTeam, mapTeamsAndOrgsToGroupedBySeason } from "./api/map-teams";
import { mapApiEvent } from "./api/event";
import { ROUTES } from "src/eden/routes";

// Returns the first errored response (shaped as a queryFn error result), or undefined if all
// succeeded. Collapses the repeated per-response error checks in the multi-fetch endpoints below.
const firstError = (...responses: { error?: unknown }[]): { error: FetchBaseQueryError } | undefined => {
  const failed = responses.find((response) => response.error);
  return failed ? { error: failed.error as FetchBaseQueryError } : undefined;
};

export const API = createApi({
  reducerPath: "API",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_SERVER_URL,
    prepareHeaders: async (headers) => {
      // Attach the Auth0 access token so the API can authenticate the user. Media
      // endpoints (avatars, HLS) are loaded directly by the browser and don't go
      // through this base query, so they remain unauthenticated by design.
      const token = await getAccessToken();
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Teams"],
  endpoints: (builder) => ({
    getMeTeams: builder.query<Team.Team[], null>({
      query: () => generatePath(ROUTES.teams.list),
      transformResponse: (response: Eden.Team[]) =>
        (response ?? []).filter((team): team is Eden.Team => team != null).map((team) => mapEdenTeamToTeam(team)),
      providesTags: ["Teams"],
    }),
    getMeTeamsGrouped: builder.query<Team.TeamListGroupedBySeason[], null>({
      query: () => generatePath(ROUTES.teams.list),
      transformResponse: (response: Eden.Team[]) => mapTeamsAndOrgsToGroupedBySeason(response ?? []),
      providesTags: ["Teams"],
    }),
    getTeamByID: builder.query<Team.Team, string>({
      query: (id) => generatePath(ROUTES.teams.team, { id: id }),
      transformResponse: (response: Eden.Team) => mapEdenTeamToTeam(response),
    }),
    getTeamUsersByID: builder.query<Eden.AssociatedTeamUser[], string>({
      query: (id) => generatePath(ROUTES.teams.users, { id: id }),
    }),
    getTeamPlayerByID: builder.query<Team.Player, { id: string; pid: string }>({
      query: (args) => generatePath(ROUTES.teams.player, { id: args.id, pid: args.pid }),
      transformResponse: (response: Eden.Player) => response,
    }),
    getTeamPlayerStatsByID: builder.query<Eden.PlayerStats, { id: string; pid: string }>({
      query: (args) => generatePath(ROUTES.teams.playerStats, { id: args.id, pid: args.pid }),
    }),
    getTeamVideoAssetsByID: builder.query<Team.VideoAsset[], string>({
      query: (id) => generatePath(ROUTES.teams.assets, { id: id }),
      transformResponse: (response: Eden.VideoAsset[]) => response as Team.VideoAsset[],
    }),
    getTeamOpponentsByID: builder.query<Team.Opponent[], string>({
      query: (id) => generatePath(ROUTES.teams.opponents, { id: id }),
      transformResponse: (response: Eden.Opponent[]) => response as Team.Opponent[],
    }),
    getTeamSprayChartByID: builder.query<Eden.EventStats[], string>({
      query: (id) => generatePath(ROUTES.teams.sprayChart, { id: id }),
    }),
    addTeamByID: builder.mutation<Team.Team, string>({
      query: (id) => ({
        url: generatePath(ROUTES.teams.team, { id: id }),
        method: "POST",
      }),
      invalidatesTags: ["Teams"],
    }),
    followTeamPublic: builder.mutation<void, string>({
      query: (id) => ({
        url: generatePath(ROUTES.teams.publicFollow, { publicID: id }),
        method: "POST",
      }),
    }),
    followTeamPrivate: builder.mutation<void, string>({
      query: (id) => ({
        url: generatePath(ROUTES.teams.privateFollow, { id: id }),
        method: "POST",
      }),
    }),
    unfollowTeam: builder.mutation<void, string>({
      query: (id) => ({
        url: generatePath(ROUTES.teams.unfollow, { id: id }),
        method: "DELETE",
      }),
    }),
    deleteTeam: builder.mutation<void, string>({
      query: (id) => ({
        url: generatePath(ROUTES.teams.team, { id: id }),
        method: "DELETE",
      }),
    }),
    patchTeamByID: builder.mutation<Team.Team, string>({
      query: (id) => ({
        url: generatePath(ROUTES.teams.team, { id: id }),
        method: "PATCH",
      }),
      invalidatesTags: ["Teams"],
    }),
    searchTeamByName: builder.mutation<Eden.SearchResults, Team.TeamSearch>({
      query: (team) => ({
        url: generatePath(ROUTES.search),
        method: "POST",
        body: team,
      }),
    }),
    // Schedule + results for a search result so the user can confirm they're importing the
    // right team. Reads directly from GameChanger (no import/follow needed); see the backend
    // SearchController GET /search/:id/schedule.
    getSearchTeamSchedule: builder.query<Eden.SchedulePreviewGame[], string>({
      query: (id) => generatePath(ROUTES.searchSchedule, { id: id }),
    }),
    // Search players by name across our imported data (our rosters + recorded opponents). The
    // backend scans Mongo because GameChanger has no player-search endpoint. Returns empty for
    // queries under 2 chars. See SearchController GET /search/players.
    searchPlayers: builder.query<Eden.PlayerSearchResults, string>({
      query: (query) => `${generatePath(ROUTES.searchPlayers)}?query=${encodeURIComponent(query)}`,
    }),
    getTeamEventAndSummariesByID: builder.query<Event.Event[], string>({
      queryFn: async (id, _queryApi, _extraOptions, fetchWithBQ) => {
        const [scheduleResponse, summariesResponse] = await Promise.all([
          fetchWithBQ(generatePath(ROUTES.teams.schedule, { id: id })),
          fetchWithBQ(generatePath(ROUTES.teams.summaries, { id: id })),
        ]);

        const error = firstError(scheduleResponse, summariesResponse);
        if (error) return error;

        const events: Eden.Event[] = (await scheduleResponse.data) as Eden.Event[];
        const summaries: Eden.GameData[] = (await summariesResponse.data) as Eden.GameData[];

        const mappedEvents = events.map((current) => {
          const currEvt = mapApiEvent({ event: current.event, pregame_data: current.pregame_data || {} });
          const gameSummary = summaries.find((g) => g.event_id === currEvt.id);

          if (gameSummary) {
            currEvt.gameData = {
              game_id: Event.toGameID(currEvt.id),
              event_id: Event.toEventID(currEvt.id),
              game_state: Event.liveStatusToGameState(gameSummary.game_status),
              opponent_score: gameSummary.opponent_team_score,
              team_score: gameSummary.owning_team_score,
              last_time_to_score_ts: gameSummary.last_time_to_score_ts,
            };
          }
          return currEvt;
        });

        return { data: mappedEvents };
      },
    }),
    getTeamEventWithSummaryByID: builder.query<Event.Event, { teamID: string; eventID: string }>({
      queryFn: async (_args, _queryApi, _extraOptions, fetchWithBQ) => {
        const [eventResponse, summaryResponse] = await Promise.all([
          fetchWithBQ(generatePath(ROUTES.teams.events.event, { id: _args.teamID, eid: _args.eventID })),
          fetchWithBQ(generatePath(ROUTES.teams.events.summary, { id: _args.teamID, eid: _args.eventID })),
        ]);

        const error = firstError(eventResponse, summaryResponse);
        if (error) return error;

        const event: Eden.Event = (await eventResponse.data) as Eden.Event;
        const summary: Eden.GameData = (await summaryResponse.data) as Eden.GameData;
        const currEvt = mapApiEvent({ event: event.event, pregame_data: event.pregame_data || {} });
        if (summary) {
          currEvt.gameData = {
            game_id: Event.toGameID(currEvt.id),
            event_id: Event.toEventID(currEvt.id),
            game_state: Event.liveStatusToGameState(summary.game_status),
            opponent_score: summary.opponent_team_score,
            team_score: summary.owning_team_score,
            last_time_to_score_ts: summary.last_time_to_score_ts,
          };
        }
        return { data: currEvt };
      },
    }),
    getTeamPitchSmartByID: builder.query<Team.PitchSmart, string>({
      queryFn: async (id, _queryApi, _extraOptions, fetchWithBQ) => {
        const [playerResponse, smartResponse, statsResponse] = await Promise.all([
          fetchWithBQ(generatePath(ROUTES.teams.players, { id: id })),
          fetchWithBQ(generatePath(ROUTES.teams.pitchSmart, { id: id })),
          fetchWithBQ(generatePath(ROUTES.teams.stats, { id: id })),
        ]);

        const error = firstError(playerResponse, smartResponse, statsResponse);
        if (error) return error;

        const players: Team.Player[] = (await playerResponse.data) as Team.Player[];
        const pitchSmart: Team.PitchSmartData[] = (await smartResponse.data) as Team.PitchSmartData[];
        const seasonStats: Team.SeasonStats = (await statsResponse.data) as Team.SeasonStats;

        return { data: { players: players, pitchSmart: pitchSmart, seasonStats: seasonStats } };
      },
    }),
    getTeamStatsByID: builder.query<Eden.SeasonStats, string>({
      query: (id) => generatePath(ROUTES.teams.stats, { id: id }),
    }),
    getTeamLiveGamesByID: builder.query<Eden.LiveGame[], string>({
      query: (id) => generatePath(ROUTES.teams.live, { id: id }),
    }),
    // Global Live feed: every in-progress game across all teams (drives the top-banner Live page).
    getAllLiveGames: builder.query<Eden.LiveTeamGame[], void>({
      query: () => ROUTES.live,
    }),
    getTeamEventLiveStream: builder.query<Eden.LiveVideoStatus, { teamID: string; eventID: string }>({
      query: (args) => generatePath(ROUTES.teams.events.videoStream, { id: args.teamID, eid: args.eventID }),
    }),
    getTeamEventGameStream: builder.query<Eden.GameStream, { teamID: string; eventID: string }>({
      query: (args) => generatePath(ROUTES.teams.events.gameStream, { id: args.teamID, eid: args.eventID }),
    }),
    getTeamEventOpponentGameStream: builder.query<Eden.OpponentGameStream, { teamID: string; eventID: string }>({
      query: (args) => generatePath(ROUTES.teams.events.opponentGameStream, { id: args.teamID, eid: args.eventID }),
    }),
    getTeamEventOpponentSeasonStats: builder.query<Eden.OpponentSeasonStats, { teamID: string; eventID: string }>({
      query: (args) => generatePath(ROUTES.teams.events.opponentSeasonStats, { id: args.teamID, eid: args.eventID }),
    }),
    getTeamPlayersByID: builder.query<Team.Player[], string>({
      query: (id) => generatePath(ROUTES.teams.players, { id: id }),
      transformResponse: (response: Eden.EdenPlayer[]) =>
        response.map((player) => {
          return {
            id: player.id,
            batting_side: player.bats?.batting_side,
            first_name: player.first_name,
            last_name: player.last_name,
            number: parseInt(player.number),
            person_id: player.user_id,
            status: player.status,
            team_id: player.team_id,
            throwing_hand: player.bats?.throwing_hand,
          } as Team.Player;
        }),
    }),
    getTeamEventStatsByID: builder.query<
      { eventStats: Eden.EventStats; players: Team.Player[]; opponents: Team.Player[] },
      { teamID: string; eventID: string }
    >({
      queryFn: async (_arg, _queryApi, _extraOptions, fetchWithBQ) => {
        const [statsResponse, playersResponse, opponentsReponse] = await Promise.all([
          fetchWithBQ(generatePath(ROUTES.teams.events.playerStats, { id: _arg.teamID, eid: _arg.eventID })),
          fetchWithBQ(generatePath(ROUTES.teams.players, { id: _arg.teamID })),
          fetchWithBQ(generatePath(ROUTES.teams.events.opponents, { id: _arg.teamID, eid: _arg.eventID })),
        ]);

        const error = firstError(statsResponse, playersResponse, opponentsReponse);
        if (error) return error;

        const eventStats: Eden.EventStats = (await statsResponse.data) as Eden.EventStats;
        const players: Team.Player[] = (await playersResponse.data) as Team.Player[];
        const opponents: Team.Player[] = (await opponentsReponse.data) as Team.Player[];

        return { data: { eventStats: eventStats, players: players, opponents: opponents } };
      },
    }),
  }),
});

export const {
  useGetTeamUsersByIDQuery,
  useFollowTeamPublicMutation,
  useFollowTeamPrivateMutation,
  useUnfollowTeamMutation,
  useGetTeamPlayerStatsByIDQuery,
  useGetTeamPlayerByIDQuery,
  useGetTeamSprayChartByIDQuery,
  useGetTeamOpponentsByIDQuery,
  useGetTeamVideoAssetsByIDQuery,
  useGetTeamEventStatsByIDQuery,
  useGetTeamEventWithSummaryByIDQuery,
  usePatchTeamByIDMutation,
  useDeleteTeamMutation,
  useGetTeamPitchSmartByIDQuery,
  useSearchTeamByNameMutation,
  useGetSearchTeamScheduleQuery,
  useSearchPlayersQuery,
  useGetTeamByIDQuery,
  useGetMeTeamsGroupedQuery,
  useGetMeTeamsQuery,
  useGetTeamStatsByIDQuery,
  useGetTeamPlayersByIDQuery,
  useGetTeamEventAndSummariesByIDQuery,
  useGetTeamLiveGamesByIDQuery,
  useGetAllLiveGamesQuery,
  useGetTeamEventLiveStreamQuery,
  useGetTeamEventGameStreamQuery,
  useGetTeamEventOpponentGameStreamQuery,
  useGetTeamEventOpponentSeasonStatsQuery,
  useAddTeamByIDMutation,
} = API;
